import { useEffect, useState } from 'preact/hooks';
import { fetchProfile, getMyRating, type Profile, type MyRating, CACHE_KEYS } from '../lib/api';
import { getCached } from '../lib/cache';
import { Skeleton } from '../components/Skeleton';

const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('es-DO');
}

/** Date as 'mes año' for "Miembro desde Mayo 2026". */
function fmtMonthYear(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return iso || '';
  const mon = MONTH_ES_SHORT[parseInt(m[2], 10) - 1] || '';
  return `${mon} ${m[1]}`;
}

function fmtFullDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso || '';
  const day = parseInt(m[3], 10);
  const mon = MONTH_ES_SHORT[parseInt(m[2], 10) - 1] || '';
  return `${day} ${mon} ${m[1]}`;
}

export function Perfil({ onOpenTienda }: { onOpenTienda?: () => void }) {
  const [data, setData] = useState<Profile | null>(null);
  const [rating, setRating] = useState<MyRating | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { load(); loadRating(); }, []);

  async function loadRating() {
    try { setRating(await getMyRating()); } catch { /* non-blocking — star just won't show */ }
  }

  async function load() {
    setError(null);
    const cached = getCached<Profile>(CACHE_KEYS.profile);
    if (cached) {
      setData(cached.value);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const r = await fetchProfile();
      setData(r);
    } catch (e) {
      if (!cached) setError((e as Error).message || 'No pudimos cargar tu perfil');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Perfil</h1></header>
        <div class="saldo-card" style={{ textAlign: 'left' }}>
          <Skeleton width={70} height={11} style={{ marginBottom: 8 }} />
          <Skeleton width="60%" height={22} style={{ marginBottom: 10 }} />
          <Skeleton width="40%" height={13} />
        </div>
        <div class="section-header">Estadísticas</div>
        <div class="card">
          {[0, 1].map((i) => (
            <div key={i} class="ledger-row">
              <div class="ledger-info" style={{ flex: 1 }}>
                <Skeleton width="55%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="35%" height={12} />
              </div>
              <Skeleton width={90} height={14} />
            </div>
          ))}
        </div>
        <div class="section-header">Mi tarjeta El Encuentro</div>
        <Skeleton height={110} borderRadius={12} />
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  if (!data) return null;

  const { info, stats } = data;
  const flagChips: string[] = [];
  if (info.flags.esLider) flagChips.push('Líder');
  if (info.flags.puedeSerSupervisor) flagChips.push('Supervisor');
  if (info.flags.puedeSerCapitan) flagChips.push('Capitán');

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Perfil</h1>
      </header>

      {/* Info card */}
      <div class="saldo-card" style={{ textAlign: 'left' }}>
        <div class="saldo-lbl" style={{ marginBottom: 4 }}>{info.personalId}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{info.nombre || '—'}</div>
          {rating && rating.visible && rating.score != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} title="Tu calificación general">
              <span style={{ color: 'var(--brand-gold)', fontSize: '1.2rem', lineHeight: 1 }}>★</span>
              <span style={{ fontWeight: 700, fontSize: '1.2rem' }}>{rating.score.toFixed(1)}</span>
            </div>
          )}
        </div>
        {info.tipoEmpleo && (
          <div class="saldo-sub">{info.tipoEmpleo}</div>
        )}
        {flagChips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {flagChips.map(label => (
              <span
                key={label}
                style={{
                  background: 'var(--color-secondary)',
                  color: 'var(--color-text)',
                  padding: '3px 10px',
                  borderRadius: 12,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                }}
              >
                {label}
              </span>
            ))}
          </div>
        )}
        {info.fechaInicio && (
          <div class="saldo-foot">Miembro desde {fmtMonthYear(info.fechaInicio)}</div>
        )}
      </div>

      {/* Tienda de uniformes — opens the full-screen store */}
      {onOpenTienda && (
        <div class="card nav-card" onClick={onOpenTienda}>
          <div class="nav-card-ic">👕</div>
          <div class="nav-card-text">
            <div class="lt-title">Tienda de uniformes</div>
            <div class="lt-sub">Compra contra tu próximo pago</div>
          </div>
          <div class="nav-card-chev">›</div>
        </div>
      )}

      {/* Stats */}
      <div class="section-header">Estadísticas</div>
      <div class="card">
        <div class="ledger-row">
          <div class="ledger-info">
            <div class="lt-title">{stats.thisMonth.mesLabel || 'Este mes'}</div>
            <div class="lt-sub">
              {stats.thisMonth.eventCount} evento{stats.thisMonth.eventCount === 1 ? '' : 's'}
            </div>
          </div>
          <div class="ledger-amt amt-pos">RD$ {fmtNum(stats.thisMonth.totalGanado)}</div>
        </div>
        <div class="ledger-row">
          <div class="ledger-info">
            <div class="lt-title">Histórico total</div>
            <div class="lt-sub">
              {stats.career.totalEvents} evento{stats.career.totalEvents === 1 ? '' : 's'}
              {stats.career.primerEvento && ` · desde ${fmtFullDate(stats.career.primerEvento)}`}
            </div>
          </div>
          <div class="ledger-amt amt-pos">RD$ {fmtNum(stats.career.totalGanado)}</div>
        </div>
      </div>

      {/* Loyalty placeholder — real integration is MA-11 */}
      <div class="section-header">Mi tarjeta El Encuentro</div>
      <div
        class="card"
        style={{
          background: 'linear-gradient(135deg, var(--brand-gold-light) 0%, var(--brand-gold-deep) 100%)',
          color: '#fff',
          padding: 20,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>🎟️</div>
        <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 4 }}>
          El Encuentro · Programa de fidelidad
        </div>
        <div style={{ opacity: 0.9, fontSize: '0.92rem' }}>
          Tu tarjeta estará disponible pronto.
        </div>
      </div>

      {info.rolesCertificados.length > 0 && (
        <>
          <div class="section-header">Roles certificados</div>
          <div class="card">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 2px' }}>
              {info.rolesCertificados.map(r => (
                <span
                  key={r}
                  style={{
                    background: 'var(--color-secondary)',
                    color: 'var(--color-text)',
                    padding: '3px 10px',
                    borderRadius: 12,
                    fontSize: '0.85rem',
                  }}
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
